import { FileIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

interface FilePreviewContentProps {
    signedUrl: string;
    contentType: string;
}

const fetchSignedFileAsBlob = async (signedUrl: string): Promise<string | null> => {
    try {
        const res = await fetch(signedUrl);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        return blobUrl;
    } catch (error) {
        console.error('Error fetching signed file as blob:', error);
        return null;
    }
};

export function FilePreviewContent({ signedUrl, contentType }: FilePreviewContentProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [markdownContent, setMarkdownContent] = useState<string | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchContent = async () => {
            try {
                setIsLoading(true);
                setError(null);

                if (contentType === 'text/markdown') {
                    const response = await fetch(signedUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch markdown: ${response.statusText}`);
                    }
                    const text = await response.text();
                    if (isMounted) {
                        setMarkdownContent(text);
                    }
                } else {
                    const blobUrlResult = await fetchSignedFileAsBlob(signedUrl);
                    if (isMounted) {
                        setBlobUrl(blobUrlResult);
                    }
                }
            } catch (error) {
                console.error('Error fetching file content:', error);
                if (isMounted) {
                    setError(error instanceof Error ? error.message : t('filePreview.errors.loadFailed'));
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (signedUrl) {
            fetchContent();
        }

        return () => {
            isMounted = false;
        };
    }, [signedUrl, contentType, t]);

    // Clean up blob URL when component unmounts
    useEffect(() => {
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground">{t('filePreview.loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <FileIcon className="h-16 w-16 mb-4" />
                <p>{t('filePreview.errors.loadError')}</p>
                <p className="text-sm mt-2">{error}</p>
            </div>
        );
    }

    if (contentType === 'text/markdown' && markdownContent) {
        return (
            <div className="prose prose-sm max-w-none">
                <ReactMarkdown>{markdownContent}</ReactMarkdown>
            </div>
        );
    }

    if (blobUrl) {
        return (
            <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(signedUrl)}&embedded=true`}
                width="100%"
                height="600px"
                frameBorder="0"
                title={t('filePreview.iframeTitle')}
                className="rounded-md"
            />
        );
    }

    return (
        <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <FileIcon className="h-16 w-16 mb-4" />
            <p>{t('filePreview.errors.notAvailable')}</p>
        </div>
    );
}
